import { BsPersonFill, BsBuilding } from "react-icons/bs";
import CnpjUtils from "../../../../shared/utils/CnpjUtils";
import MonetaryUtils from "../../../../shared/utils/MonetaryUtils";
import {
	createCalculatedInstallmentFields,
} from "../../utils/simulacao-utils";
import { hasValue } from "../../../utils/pacto-rebates-utils";

export const inputsConfig = ({
	onCnpjComplete,
	simulation,
	shouldSearchCnpj = true,
	isViewMode = false,
	isEditMode = false,
	isRestoringSimulationState = false,
	financialInstallments = [],
	paymentConditionsLoading = false
}) => {
	const availableInstallments = Array.isArray(financialInstallments)
		? financialInstallments
		: [];

	const financialInstallmentValues = availableInstallments
		.map(item => Number(item.value))
		.filter(value => !Number.isNaN(value))
		.sort((firstValue, secondValue) => firstValue - secondValue);

	const installmentValues = financialInstallmentValues;

	const installmentsDefaultValue =
		isViewMode || isRestoringSimulationState
			? String(simulation?.installments || "")
			: "";

	const paymentConditionOptions = simulation?.paymentConditionOptions || [];

	const calculatedInstallmentFields = createCalculatedInstallmentFields(
		simulation?.calculatedInstallments,
		{
			emptyValue: ""
		}
	);

	const isBuybackEnabled = values => Boolean(values.buyback);

	return [
		{
			section: "Cliente",
			icon: BsPersonFill,
			fields: [
				{
					name: "clientDocument",
					label: "CNPJ",
					type: "text",
					placeholder: "00.000.000/0000-00",
					defaultValue: simulation?.clientDocument || "",
					columns: 4,
					mask: CnpjUtils.getAlphanumericCnpjMask(),
					guide: false,
					disabled: isViewMode || isEditMode,
					onChange: value => value.toUpperCase(),
					actions: shouldSearchCnpj
						? [
							{
								event: "complete",
								targetField: "clientName",
								isTriggered: value => {
									const cnpj = CnpjUtils.cleanAlphanumericCnpj(value);
									return /^[A-Z0-9]{12}\d{2}$/.test(cnpj);
								},
								execute: async value => {
									const cnpj = CnpjUtils.cleanAlphanumericCnpj(value);
									return onCnpjComplete(cnpj);
								},
								isSuccess: response => {
									return response?.ok && Boolean(response?.client?.clientName);
								},
								getValue: response => response?.client?.clientName || "",
								clearTargetOnChange: true,
								clearSourceOnFailure: true,
								disableTargetOnSuccess: true
							}
						]
						: []
				},
				{
					name: "clientName",
					label: "Nome (Razão Social)",
					type: "text",
					placeholder: "Razão Social ou Nome",
					defaultValue: simulation?.clientName || "",
					columns: 8,
					disabled: isViewMode || isEditMode
				}
			]
		},
		{
			section: "Equipamento e Condições",
			icon: BsBuilding,
			fields: [
				{
					name: "equipmentName",
					label: "Nome do equipamento",
					type: "text",
					defaultValue: simulation?.equipmentName || "",
					columns: 4,
					disabled: isViewMode
				},
				{
					name: "equipmentValue",
					label: "Valor (R$)",
					type: "text",
					defaultValue: simulation?.equipmentValue
						? MonetaryUtils.formatCurrency(simulation.equipmentValue)
						: "",
					placeholder: "R$ 0,00",
					columns: 4,
					onChange: value => MonetaryUtils.formatCurrency(value, { fromCents: true }),
					disabled: isViewMode
				},
				{
					name: "installments",
					label: "Parcelas",
					type: "select",
					options: installmentValues.map(installment => ({
						label: `${installment}x`,
						value: String(installment)
					})),
					defaultValue: installmentsDefaultValue,
					placeholder: "Selecione",
					columns: 4,
					disabled: isViewMode
				},
				/*{
					name: "minimumInitialPayment",
					label: "Entrada mínima",
					type: "text",
					defaultValue: hasValue(simulation?.minimumInitialPayment)
						? MonetaryUtils.formatCurrency(simulation.minimumInitialPayment)
						: "",
					placeholder: "R$ 0,00",
					columns: 4,
					disabled: true
				},
				{
					name: "maximumInitialPayment",
					label: "Entrada máxima",
					type: "text",
					defaultValue: hasValue(simulation?.maximumInitialPayment)
						? MonetaryUtils.formatCurrency(simulation.maximumInitialPayment)
						: "",
					placeholder: "R$ 0,00",
					columns: 4,
					disabled: true
				},*/
				{
					name: "paymentConditions",
					label: "Condições de Pagamento",
					type: "collapseSelect",
					options: paymentConditionOptions,
					defaultValue: simulation?.paymentConditions || "",
					placeholder: paymentConditionsLoading
						? "Carregando condições..."
						: "Selecione uma condição",
					columns: 12,
					disabled: isViewMode || paymentConditionsLoading || paymentConditionOptions.length === 0
				},
				{
					name: "downPaymentPercentage",
					label: "% de Entrada",
					type: "text",
					defaultValue: MonetaryUtils.formatPercentage(simulation?.downPaymentPercentage),
					columns: 2,
					disabled: true
				},
				{
					name: "initialPayment",
					label: "Entrada (R$)",
					type: "text",
					defaultValue: hasValue(simulation?.initialPayment)
						? MonetaryUtils.formatCurrency(simulation.initialPayment)
						: "",
					placeholder: "R$ 0,00",
					columns: 2,
					disabled: true
				},
				...calculatedInstallmentFields,
				{
					name: "buyback",
					label: "Recompra",
					type: "switch",
					defaultValue: simulation?.buyback || false,
					columns: 12,
					disabled: isViewMode
				},
				{
					name: "buybackEquipment",
					label: "Equipamento",
					type: "text",
					defaultValue: simulation?.buybackEquipment || "",
					columns: 4,
					disabled: isViewMode,
					visibleWhen: isBuybackEnabled
				},
				{
					name: "buybackValue",
					label: "Valor Recompra",
					type: "text",
					defaultValue: simulation?.buybackValue
						? MonetaryUtils.formatCurrency(simulation.buybackValue)
						: "",
					columns: 4,
					placeholder: "R$ 0,00",
					onChange: value => MonetaryUtils.formatCurrency(value, { fromCents: true }),
					disabled: isViewMode,
					visibleWhen: isBuybackEnabled
				},
				{
					name: "buybackValueInstallment",
					label: "Valor Parcela Recompra",
					type: "text",
					defaultValue: simulation?.buybackValueInstallment
						? MonetaryUtils.formatCurrency(simulation.buybackValueInstallment)
						: "",
					columns: 4,
					placeholder: "R$ 0,00",
					disabled: true,
					visibleWhen: isBuybackEnabled
				}
			]
		}
	];
};